import dayjs from "dayjs";
import React, { FC } from "react";
import { Flex } from "antd";
import { get } from "lodash";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetReportByDealerLazyQuery } from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

const DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSSZ";

type ReportViolationFormData = {
  fromCreatedAt: Date | null;
  toCreatedAt: Date | null;
  candidate?: string;
};

interface ReportViolationFormProps {
  setCloseModal: () => void;
  formId: string;
}

const ReportViolationForm: FC<ReportViolationFormProps> = ({
  setCloseModal,
  formId,
}) => {
  const { t } = useTranslation();

  const { notifyReport } = useNotify();
  const [getReportByDealerLazyQuery] = useGetReportByDealerLazyQuery({
    onCompleted(data) {
      const hash = get(data, `ViolationGetter.getReportByDealer.fileHash`);
      hash && notifyReport(hash);
      setCloseModal();
    },
    fetchPolicy: "no-cache",
  });

  const { handleSubmit, control, watch } = useForm<ReportViolationFormData>();
  const fromCreatedAtWatch = watch("fromCreatedAt");
  const toCreatedAtWatch = watch("toCreatedAt");

  const onSubmit = ({
    candidate,
    fromCreatedAt,
    toCreatedAt,
  }: ReportViolationFormData) => {
    getReportByDealerLazyQuery({
      variables: {
        dateBegin: fromCreatedAt
          ? dayjs(fromCreatedAt).format(DATE_TIME_FORMAT)
          : undefined,
        dateEnd: toCreatedAt
          ? dayjs(toCreatedAt).endOf("day").format(DATE_TIME_FORMAT)
          : undefined,
        dealerCandidate: candidate || undefined,
      },
    });
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="datePicker"
          name="fromCreatedAt"
          control={control}
          label={t("violation.dateBegin")}
          format="DD.MM.YYYY"
          placeholder={t("violationsTable.defaultDateFrom")}
          disabledDate={(current) =>
            toCreatedAtWatch
              ? current.isAfter(dayjs(toCreatedAtWatch), "day")
              : false
          }
        />
        <Field
          type="datePicker"
          name="toCreatedAt"
          control={control}
          label={t("violation.dateEnd")}
          format="DD.MM.YYYY"
          placeholder={t("violationsTable.defaultDateTo")}
          disabledDate={(current) =>
            fromCreatedAtWatch
              ? current.isBefore(dayjs(fromCreatedAtWatch), "day")
              : false
          }
        />
        <Field
          type="select"
          name="candidate"
          control={control}
          label={t("violationsTable.dealerCandidate")}
          allowClear
          options={[
            { value: "active", label: t("violationsTable.activeCandidate") },
            { value: "candidate", label: t("violationsTable.selectCandidate") },
          ]}
        />
      </Flex>
    </form>
  );
};

export default ReportViolationForm;
